"use client";

import { AppBar, CircularProgress, Tab } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useParams } from "react-router-dom";

import Tabs from "@/components/atoms/ZoomAwareTabs";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useGetWorkflowById from "@/hooks/requests/wokflow/use-get-workflow-by-id";
import useTabs from "@/hooks/use-tabs";
import { WorkflowMessages } from "@/messages/workflow/workflow.messages";

import WorkflowFormTab from "./components/WorkflowFormTab";
import WorkflowSettingsTab from "./components/WorkflowSettingsTab";
import WorkflowStepsTab from "./components/WorkflowStepsTab";
import { TABS_ITEMS } from "./constants";
import { WorkflowTabs } from "./types";

type WorkflowFormPageProps = {
  messages: WorkflowMessages;
  isEditing?: boolean;
};

export default function WorkflowFormPage({ messages }: WorkflowFormPageProps) {
  const { workflowId } = useParams();
  const { lang } = useLanguage();
  const _id = Number(workflowId) || 0;
  const { data: workflow, isLoading: isLoadingWorkflow, error } = useGetWorkflowById({ _id });
  const TABS = TABS_ITEMS(messages);
  const initialTab = "workflowTab";
  const tabs = useTabs<WorkflowTabs>({ initialTab, tabs: TABS, isEditing: true });
  const { activeTab, changeTab, isTabDisabled, tabsOrientation } = tabs;

  const shouldLoadError = !workflow && !!error && !isLoadingWorkflow;
  const BREAD_CRUMBS = [{ title: messages.breadCrumbTitle, href: ROUTE_URLS.workflow(lang) }];

  const isWorkflowTab = activeTab === "workflowTab" && workflow;
  const isWorkflowStepTab = activeTab === "workflowStepsTab" && workflow;
  const isWorkflowSettingsTab = activeTab === "workflowSettingsTab" && workflow;

  return (
    <PageLayout title={messages.pageTitle} breadCrumbs={BREAD_CRUMBS} messages={messages}>
      <PageContainer>
        <Suspense isSuspended={!!isLoadingWorkflow} fallback={<CircularProgress size={50} />}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 12, md: "auto", lg: "auto" }}>
              <AppBar position="static">
                <Tabs
                  orientation={tabsOrientation}
                  value={activeTab}
                  onChange={(_, newTab) => {
                    if (!isTabDisabled(newTab)) changeTab(newTab);
                  }}
                  sx={{ width: "100%" }}
                >
                  {TABS.map((tab, index) => (
                    <Tab
                      key={index}
                      sx={{ px: 2 }}
                      label={tab.label}
                      value={tab.value}
                      disabled={isTabDisabled(tab.value)}
                    />
                  ))}
                </Tabs>
              </AppBar>
            </Grid>
          </Grid>

          {isWorkflowTab && <WorkflowFormTab workflow={workflow} messages={messages} />}

          {isWorkflowStepTab && <WorkflowStepsTab workflow={workflow} messages={messages} />}

          {isWorkflowSettingsTab && <WorkflowSettingsTab workflow={workflow} messages={messages} />}

          {!!shouldLoadError && (
            <AlertBlock
              title={messages.workflowNotFoundError}
              description={error.message}
              alt={messages.workflowNotFoundAlt}
            />
          )}
        </Suspense>
      </PageContainer>
    </PageLayout>
  );
}
